import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CookieConsentBanner = ({ onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false
  });

  const handleAcceptAll = () => {
    setCookiePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
    
    // Store consent in localStorage
    localStorage.setItem('cookie_consent', JSON.stringify({
      timestamp: new Date()?.toISOString(),
      preferences: {
        essential: true,
        analytics: true,
        marketing: true,
        preferences: true
      }
    }));
    
    onClose();
  };

  const handleAcceptSelected = () => {
    // Store selected preferences in localStorage
    localStorage.setItem('cookie_consent', JSON.stringify({
      timestamp: new Date()?.toISOString(),
      preferences: cookiePreferences
    }));
    
    onClose();
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      essential: true, // Essential cookies cannot be rejected
      analytics: false,
      marketing: false,
      preferences: false
    });
    
    // Store minimal consent in localStorage
    localStorage.setItem('cookie_consent', JSON.stringify({
      timestamp: new Date()?.toISOString(),
      preferences: {
        essential: true,
        analytics: false,
        marketing: false,
        preferences: false
      }
    }));
    
    onClose();
  };

  const handlePreferenceChange = (type, value) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for basic site functionality, authentication, and security. Cannot be disabled.',
      icon: 'Shield',
      required: true
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how you use our site to improve performance and user experience.',
      icon: 'BarChart3',
      required: false
    },
    {
      id: 'preferences',
      name: 'Preference Cookies',
      description: 'Remember your settings and choices to personalize your experience.',
      icon: 'Settings',
      required: false
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.',
      icon: 'Target',
      required: false
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Basic Cookie Notice */}
        {!showDetails && (
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start flex-1">
              <Icon name="Cookie" size={24} className="text-blue-600 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  We use cookies to enhance your experience
                </h3>
                <p className="text-sm text-gray-600">
                  We use cookies and similar technologies to provide functionality, improve performance, 
                  and deliver personalized content. By continuing to use our site, you consent to our use of cookies.
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowDetails(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
              >
                Customize Settings
              </button>
              
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Reject All
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
        )}

        {/* Detailed Cookie Preferences */}
        {showDetails && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Cookie" size={24} className="text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Cookie Preferences
                </h3>
              </div>
              
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="grid gap-4">
              {cookieTypes?.map((cookieType) => (
                <div key={cookieType?.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <Icon 
                        name={cookieType?.icon} 
                        size={20} 
                        className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" 
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {cookieType?.name}
                          {cookieType?.required && (
                            <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              Required
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {cookieType?.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cookiePreferences?.[cookieType?.id]}
                          onChange={(e) => handlePreferenceChange(cookieType?.id, e?.target?.checked)}
                          disabled={cookieType?.required}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                <a 
                  href="/legal-terms-privacy" 
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Learn more about our cookie policy
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reject All
                </button>
                
                <button
                  onClick={handleAcceptSelected}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Preferences
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;